import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLScreenReaders = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Accessibility (A11Y)</div>
                <h1>Optimizing for Screen Readers</h1>
                <p className="hero-lead">
                    Make your websites accessible to blind and visually impaired users. Learn how screen
                    readers work and how to optimize your HTML for the best screen reader experience.
                </p>
            </div>

            <div className="content-section">
                <h2>How Screen Readers Work</h2>
                <p>
                    Screen readers convert text and page structure into speech or braille. They navigate
                    through HTML semantics, ARIA attributes, and heading hierarchies.
                </p>

                <div className="sr-overview">
                    <h3>Popular Screen Readers:</h3>
                    <ul>
                        <li><strong>NVDA:</strong> Free, Windows (most popular)</li>
                        <li><strong>JAWS:</strong> Commercial, Windows (enterprise standard)</li>
                        <li><strong>VoiceOver:</strong> Built-in, macOS/iOS</li>
                        <li><strong>TalkBack:</strong> Built-in, Android</li>
                        <li><strong>Narrator:</strong> Built-in, Windows</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Page Structure for Screen Readers</h2>

                <h3>Landmarks for Navigation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Screen readers can jump between landmarks --&gt;
<header role="banner">
    <nav role="navigation" aria-label="Main navigation">
        <!-- Navigation --&gt;
    </nav>
</header>

<main role="main">
    <!-- Main content --&gt;
</main>

<aside role="complementary">
    <!-- Sidebar --&gt;
</aside>

<footer role="contentinfo">
    <!-- Footer --&gt;
</footer>

<!-- Users can: --&gt;
<!-- - List all landmarks --&gt;
<!-- - Jump directly to main content --&gt;
<!-- - Skip repetitive navigation --&gt;`} />
                </div>

                <h3>Skip Links</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<body>
    <!-- Skip link (usually hidden visually) --&gt;
    <a href="#main-content" class="skip-link">
        Skip to main content
    </a>
    
    <header>
        <nav><!-- Lots of navigation links --&gt;</nav>
    </header>
    
    <main id="main-content">
        <!-- Main content here --&gt;
    </main>
</body>

<style>
.skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: #000;
    color: white;
    padding: 8px;
    text-decoration: none;
    z-index: 100;
}

.skip-link:focus {
    top: 0; /* Show on keyboard focus */
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Heading Hierarchy</h2>
                <p>
                    Screen reader users often navigate by headings. Proper hierarchy is crucial.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Good Heading Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Page Title</h1>

<h2>Section 1</h2>
<p>Content...</p>

<h3>Subsection 1.1</h3>
<p>Content...</p>

<h3>Subsection 1.2</h3>
<p>Content...</p>

<h2>Section 2</h2>
<p>Content...</p>

<!-- Users can: --&gt;
<!-- - List all headings --&gt;
<!-- - Jump to any heading --&gt;
<!-- - Understand content structure --&gt;`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span> Bad: Skipping Levels</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Page Title</h1>
<h4>Subsection</h4>  <!--  Skipped h2 and h3 --&gt;
<h2>Section</h2>     <!--  Wrong order --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Links and Buttons</h2>

                <h3>Descriptive Link Text</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span> Bad: Vague Link Text</span>
                    </div>
                    <CodeBlock language="html" code={`<a href="/report.pdf">Click here</a>
<a href="/more">Read more</a>
<a href="/learn">Learn more</a>

<!-- Out of context, these mean nothing! --&gt;
<!-- Screen readers can list all links --&gt;`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Good: Descriptive Links</span>
                    </div>
                    <CodeBlock language="html" code={`<a href="/report.pdf">Download 2024 Annual Report (PDF, 2MB)</a>
<a href="/features">Read more about our features</a>
<a href="/html-guide">Learn more about HTML basics</a>

<!-- Clear and meaningful out of context --&gt;`} />
                </div>

                <h3>Button vs Link</h3>
                <div className="code-example">
                    <pre><code>{`<!-- Use links for navigation --&gt;
<a href="/page">Go to page</a>

<!-- Use buttons for actions --&gt;
<button onclick="saveData()">Save</button>
<button type="submit">Submit form</button>

<!-- Screen readers announce role differently:
   "Link: Go to page"
   "Button: Save" --&gt;`}</code></pre>
                </div>
            </div>

            <div className="content-section">
                <h2>Images and Alt Text</h2>

                <h3>Informative Images</h3>
                <div className="code-example">
                    <pre><code>{`<!-- Describe what's in the image --&gt;
<img src="chart.png" 
     alt="Bar chart showing 45% increase in sales from Q3 to Q4 2024">

<!-- Include text from image --&gt;
<img src="quote.png" 
     alt="Quote: The best way to predict the future is to invent it">`}</code></pre>
                </div>

                <h3>Decorative Images</h3>
                <div className="code-example">
                    <pre><code>{`<!-- Empty alt for decorative images --&gt;
<img src="decorative-line.png" alt="">

<!-- Or use aria-hidden --&gt;
<img src="decoration.png" aria-hidden="true" alt="">

<!-- Screen reader skips these --&gt;`}</code></pre>
                </div>

                <h3>Complex Images</h3>
                <div className="code-example">
                    <pre><code>{`<!-- Use longdesc or detailed description --&gt;
<img src="complex-diagram.png" 
     alt="Website architecture diagram"
     aria-describedby="diagram-desc">

<div id="diagram-desc">
    <p>The diagram shows three layers:</p>
    <ul>
        <li>Frontend: React application</li>
        <li>Backend: Node.js API server</li>
        <li>Database: PostgreSQL database</li>
    </ul>
</div>`}</code></pre>
                </div>
            </div>

            <div className="content-section">
                <h2>Forms for Screen Readers</h2>

                <h3>Labels and Instructions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always use labels --&gt;
<label for="email">Email address:</label>
<input type="email" id="email" name="email" required>

<!-- Provide instructions --&gt;
<label for="password">
    Password:
    <span class="help-text">(Must be 8+ characters)</span>
</label>
<input type="password" 
       id="password" 
       aria-describedby="password-help">
<div id="password-help">
    Must contain at least one number and one uppercase letter
</div>`} />
                </div>

                <h3>Error Messages</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="email">Email:</label>
<input type="email" 
       id="email" 
       aria-invalid="true"
       aria-describedby="email-error">
<div id="email-error" role="alert">
    Please enter a valid email address
</div>

<!-- role="alert" makes it announced immediately --&gt;`} />
                </div>

                <h3>Grouping Related Fields</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<fieldset>
    <legend>Contact Information</legend>
    
    <label for="name">Name:</label>
    <input type="text" id="name">
    
    <label for="email">Email:</label>
    <input type="email" id="email">
</fieldset>

<!-- Screen reader announces: --&gt;
<!-- "Contact Information, Name, edit text" --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Tables for Screen Readers</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<table>
    <caption>Monthly Sales Report</caption>
    <thead>
        <tr>
            <th scope="col">Month</th>
            <th scope="col">Sales</th>
            <th scope="col">Growth</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <th scope="row">January</th>
            <td>$45,000</td>
            <td>+12%</td>
        </tr>
    </tbody>
</table>

<!-- Screen reader announces: --&gt;
<!-- "Table: Monthly Sales Report, 3 columns, 2 rows" --&gt;
<!-- "Month column header, January row header, $45,000" --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Dynamic Content</h2>

                <h3>Live Regions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Status messages --&gt;
<div aria-live="polite" aria-atomic="true">
    <p id="status">Ready</p>
</div>

<script>
// Updates are announced
status.textContent = 'Saving...';
setTimeout(() => {
    status.textContent = 'Saved successfully!';
}, 2000);
</script>

<!-- aria-live values: --&gt;
<!-- polite: Wait for user to pause --&gt;
<!-- assertive: Interrupt immediately (use sparingly!) --&gt;
<!-- off: No announcements --&gt;`} />
                </div>

                <h3>Loading States</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button aria-busy="false" id="submit-btn">
    Submit
</button>

<script>
button.onclick = async () => {
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Loading...';
    
    await submitForm();
    
    button.setAttribute('aria-busy', 'false');
    button.textContent = 'Submit';
};
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing with Screen Readers</h2>

                <h3>NVDA Basics (Windows)</h3>
                <div className="keyboard-shortcuts">
                    <ul>
                        <li><kbd>Insert + Down Arrow</kbd> - Read from cursor</li>
                        <li><kbd>Insert + T</kbd> - Read title</li>
                        <li><kbd>H</kbd> - Next heading</li>
                        <li><kbd>Shift + H</kbd> - Previous heading</li>
                        <li><kbd>K</kbd> - Next link</li>
                        <li><kbd>F</kbd> - Next form field</li>
                        <li><kbd>T</kbd> - Next table</li>
                        <li><kbd>D</kbd> - Next landmark</li>
                        <li><kbd>Insert + F7</kbd> - List all elements</li>
                    </ul>
                </div>

                <h3>VoiceOver Basics (Mac)</h3>
                <div className="keyboard-shortcuts">
                    <ul>
                        <li><kbd>Cmd + F5</kbd> - Toggle VoiceOver</li>
                        <li><kbd>VO + A</kbd> - Start reading</li>
                        <li><kbd>VO + Right/Left Arrow</kbd> - Navigate</li>
                        <li><kbd>VO + Cmd + H</kbd> - Next heading</li>
                        <li><kbd>VO + Cmd + L</kbd> - Next link</li>
                        <li><kbd>VO + U</kbd> - Rotor (list elements)</li>
                    </ul>
                    <p><em>VO = Control + Option</em></p>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Pitfalls</h2>

                <div className="pitfalls-list">
                    <h3> Pitfall 1: CSS-Only Content</h3>
                    <div className="code-example error">
                        <CodeBlock language="css" code={`.icon::before {
    content: "Important: ";
}

<!-- Screen readers may not announce ::before content --&gt;`} />
                    </div>

                    <h3> Pitfall 2: Placeholder as Label</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<input type="text" placeholder="Enter name">
<!--  No label! Placeholder disappears on input --&gt;`} />
                    </div>

                    <h3> Pitfall 3: Div/Span Buttons</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<div onclick="submit()">Submit</div>
<!--  Not accessible, no keyboard support --&gt;`} />
                    </div>

                    <h3> Pitfall 4: Auto-playing Content</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<video autoplay>
<!--  Disrupts screen reader users --&gt;`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Checklist</h2>
                <div className="checklist">
                    <ul>
                        <li> Use semantic HTML elements</li>
                        <li> Maintain logical heading hierarchy</li>
                        <li> Provide skip links</li>
                        <li> Use descriptive link text</li>
                        <li> Add alt text to all images</li>
                        <li> Label all form inputs</li>
                        <li> Use fieldset for related fields</li>
                        <li> Announce dynamic changes with aria-live</li>
                        <li> Make all interactive elements keyboard accessible</li>
                        <li> Test with actual screen readers</li>
                        <li> Avoid CSS-only important content</li>
                        <li> Don't auto-play media</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLScreenReaders;




